import { onMounted, onUnmounted, ref } from 'vue';
import { createPointerOrbitDetector } from '@/utils/pointerOrbitDetector';

const DIZZY_DURATION_MS = 2_300;
const DIZZY_COOLDOWN_MS = 1_600;

export function useMascotPointerInteraction() {
  const mascotRoot = ref(null);
  const eyeLeft = ref(null);
  const eyeRight = ref(null);
  const leftPupilPos = ref({ x: 0, y: 0 });
  const rightPupilPos = ref({ x: 0, y: 0 });
  const blobCardTiltStyle = ref({});
  const isDizzy = ref(false);
  const orbitDetector = createPointerOrbitDetector();
  let dizzyTimer = 0;
  let cooldownUntil = 0;
  let reduceMotionQuery = null;

  function pupilOffset(eyeElement, event) {
    const rect = eyeElement.getBoundingClientRect();
    const deltaX = event.clientX - (rect.left + rect.width / 2);
    const deltaY = event.clientY - (rect.top + rect.height / 2);
    const angle = Math.atan2(deltaY, deltaX);
    const distance = Math.min(5, Math.hypot(deltaX, deltaY) / 16);
    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
    };
  }

  function triggerDizzy() {
    if (isDizzy.value || reduceMotionQuery?.matches) return;
    isDizzy.value = true;
    blobCardTiltStyle.value = {};
    window.clearTimeout(dizzyTimer);
    dizzyTimer = window.setTimeout(() => {
      isDizzy.value = false;
      cooldownUntil = performance.now() + DIZZY_COOLDOWN_MS;
      orbitDetector.reset();
    }, DIZZY_DURATION_MS);
  }

  function handleMouseMove(event) {
    if (eyeLeft.value && eyeRight.value && !isDizzy.value) {
      leftPupilPos.value = pupilOffset(eyeLeft.value, event);
      rightPupilPos.value = pupilOffset(eyeRight.value, event);
    }

    if (!isDizzy.value) {
      const tiltX = ((event.clientY - window.innerHeight / 2) / window.innerHeight) * -6;
      const tiltY = ((event.clientX - window.innerWidth / 2) / window.innerWidth) * 6;
      blobCardTiltStyle.value = {
        transform: `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)`,
        transition: 'transform 0.1s ease-out',
      };
    }

    const root = mascotRoot.value;
    if (!root || isDizzy.value || reduceMotionQuery?.matches || performance.now() < cooldownUntil) return;
    const rect = root.getBoundingClientRect();
    if (orbitDetector.push(
      { x: event.clientX, y: event.clientY },
      { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
      performance.now(),
    )) triggerDizzy();
  }

  onMounted(() => {
    reduceMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
  });

  onUnmounted(() => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.clearTimeout(dizzyTimer);
    orbitDetector.reset();
  });

  return {
    mascotRoot,
    eyeLeft,
    eyeRight,
    leftPupilPos,
    rightPupilPos,
    blobCardTiltStyle,
    isDizzy,
  };
}

